import { notify } from "@dehub/theme";
import i18n from "i18next";
import { humanizeError } from "./humanize-error";

interface ErrorMessage {
  code?: string;
  error?: string;
  message?: string;
  [key: string]: any;
}

const TOAST_DEDUPE_WINDOW_MS = 3000;
const recentToasts = new Map<string, number>();

// Keyed on the text the user ends up reading, not on the status/code that
// produced it: several distinct failures now collapse to the same sentence,
// and stacking that sentence twice just looks broken.
function shouldShowToast(message: string): boolean {
  const key = message;
  const now = Date.now();
  const last = recentToasts.get(key);

  recentToasts.forEach((ts, k) => {
    if (now - ts > TOAST_DEDUPE_WINDOW_MS) recentToasts.delete(k);
  });

  if (last && now - last < TOAST_DEDUPE_WINDOW_MS) return false;
  recentToasts.set(key, now);
  return true;
}

export class HttpHandler extends Error {
  statusCode: number;
  code?: string;
  error?: string;
  payload?: any;

  constructor(statusCode: number, errorMessage?: ErrorMessage) {
    super(errorMessage?.message || "Unknown error");
    this.name = "HttpHandler";
    this.statusCode = statusCode;
    this.code = errorMessage?.code;
    this.error = errorMessage?.error;
    this.payload = errorMessage;

    Object.setPrototypeOf(this, HttpHandler.prototype);
  }

  toString() {
    return `${this.name}: ${this.message}`;
  }

  async handle(res: Response, silent = false) {
    let data: any = null;

    const raw = await res.text().catch(() => "");
    if (raw) {
      try {
        data = JSON.parse(raw);
      } catch {
        data = null;
      }
    }

    if (res.ok) return data;

    const error = new HttpHandler(
      this.statusCode,
      typeof data === "object"
        ? data
        : {
          message: data || "Unknown error",
          error: "UNKNOWN_ERROR",
        },
    );

    // This is where the toast the user actually sees is built, so it is where
    // the guarantee has to hold: an untranslated key, an English error-class
    // default or a service URL must never make it into the notification.
    const message = humanizeError(error?.message, error.code);

    if (!silent && shouldShowToast(message)) {
      notify.error({ title: i18n.t("error.title", { ns: "common" }), message });
    }

    // 401-ийн үед session-ийг цэвэрлэх/login руу шилжих логик нь errorHandler
    // (utils/index.ts) дотор төвлөрсөн бөгөөд зөвхөн auth service (/2fa/auth/*)
    // 401-д л ажиллана. Энд зүгээр алдааг дамжуулна — бизнес-модулийн 401
    // тухайн хүсэлтийг л унагаана, бүх session-ийг устгахгүй.
    throw error;
  }
}
